// Copyright (c) 2022 by Move Closer

import { Injectable } from '@movecloser/front-core'

import {
  AggregatesRelated,
  ExposesRelatedRecord,
  PossibleTypeDriver,
  RelatedRecord,
  RelatedTypeDriverRegistry
} from './service.contracts'
import { isRelated } from './helpers'
import { RelatedType } from './data.contracts'

/**
 * @author Łukasz Sitnicki <lukasz.sitnicki@movecloser.pl>
 */
@Injectable()
export abstract class AbstractRelatedService implements AggregatesRelated, ExposesRelatedRecord {
  /**
   * Record of `RelatedTypeDrivers`.
   */
  protected drivers: RelatedTypeDriverRegistry = {}

  /**
   * Record of all related data.
   */
  protected record: RelatedRecord = {}

  constructor (drivers: RelatedTypeDriverRegistry, record?: RelatedRecord) {
    this.drivers = drivers

    if (record) {
      this.record = record
    }
  }

  /**
   * @inheritDoc
   */
  public getRecord (): RelatedRecord {
    return this.record
  }

  /**
   * @inheritDoc
   */
  public storeRelated (record: RelatedRecord | unknown): void {
    if (typeof record !== 'object' || record === null) {
      return
    }

    for (const [key, value] of Object.entries(record)) {
      if (key in this.record && typeof this.record[key as RelatedType] !== 'undefined') {
        this.record[key as RelatedType] = { ...this.record[key as RelatedType], ...value }
      } else {
        this.record[key as RelatedType] = value
      }
    }
  }

  /**
   * Resolves driver based on the passed-in `RelatedType`.
   */
  protected resolveDriver (type: RelatedType): PossibleTypeDriver {
    const availableDrivers: string[] = []
    for (const key in this.drivers) {
      if (typeof this.drivers[key] === 'undefined') {
        continue
      }
      availableDrivers.push(key)
    }

    if (!availableDrivers.includes(type)) {
      throw new Error(`[RelatedService]: Unsupported related type [${type}] given.`)
    }

    const found = this.drivers[type] as PossibleTypeDriver
    for (const t of found.dependentTypes()) {
      if (!availableDrivers.includes(t)) {
        throw new Error(`[RelatedService]: Unsupported nested type (${t}) of (${type}).`)
      }
    }

    return found
  }

  /**
   * Validates the passed-in `related` argument against the `Related` interface.
   */
  protected validateRelated (related: unknown): void {
    if (!isRelated(related)) {
      throw new Error(
        `[RelatedService]: Provided value is NOT compliant with the Related interface! Value of the (related) argument: ${JSON.stringify(related)}.`
      )
    }
  }
}
